/**
 * SPLIT TABLE DRAFT SERVICE - Manages draft/pending split table sessions
 * Saves incomplete splits to Firestore so they can be resumed later
 */

import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  getDocs,
  query,
  where,
  deleteDoc,
  serverTimestamp,
  onSnapshot
} from '../services/firestoreCompat';
import { db } from './firebaseConfig';

export interface SplitTableDraft {
  id?: string;
  session_id: string;
  table_id: number;
  table_number: string;
  order_id: string;
  original_order_total: number;
  status: 'draft' | 'pending' | 'completed' | 'cancelled';
  active_step: number;
  people: Array<{
    id: string;
    name: string;
    email?: string;
    phone?: string;
    items: Array<{
      id: string;
      menuItemId: string;
      name: string;
      price: number;
      quantity: number;
      totalPrice: number;
    }>;
    subtotal: number;
    tax: number;
    tip: number;
    total: number;
    paymentStatus: 'pending' | 'partial' | 'paid';
    payments: Array<{
      paymentMethod: string;
      amount: number;
      tip: number;
      totalAmount: number;
    }>;
  }>;
  selected_items: Array<{
    id: string;
    menuItemId: string;
    name: string;
    price: number;
    quantity: number;
    totalPrice: number;
  }>;
  custom_amounts: { [personId: string]: number };
  payment_methods: { [personId: string]: string };
  tip_amount: number;
  custom_tip: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
  last_accessed_at?: string;
}

class SplitTableDraftService {
  private static instance: SplitTableDraftService;
  private readonly COLLECTION_NAME = 'split_table_drafts';

  private constructor() {
    console.log('💾 Split Table Draft Service initialized');
  }

  public static getInstance(): SplitTableDraftService {
    if (!SplitTableDraftService.instance) {
      SplitTableDraftService.instance = new SplitTableDraftService();
    }
    return SplitTableDraftService.instance;
  }

  /**
   * Save or update a draft split session to Firestore
   */
  public async saveDraft(draft: Omit<SplitTableDraft, 'id' | 'created_at' | 'updated_at'>): Promise<string> {
    try {
      console.log('💾 [DRAFT] Saving draft split session to Firestore:', {
        session_id: draft.session_id,
        table_number: draft.table_number,
        status: draft.status,
        active_step: draft.active_step,
        people_count: draft.people.length,
        total: draft.original_order_total
      });
      
      // Check if draft already exists
      const existingDraft = await this.getDraftBySessionId(draft.session_id);
      
      // Helper function to remove undefined values (Firestore doesn't allow undefined)
      const removeUndefined = (obj: any): any => {
        if (obj === null || obj === undefined) return null;
        if (Array.isArray(obj)) {
          return obj.map(item => removeUndefined(item));
        }
        if (typeof obj === 'object') {
          const cleaned: any = {};
          for (const key in obj) {
            if (obj[key] !== undefined) {
              cleaned[key] = removeUndefined(obj[key]);
            }
          }
          return cleaned;
        }
        return obj;
      };

      const draftData: any = removeUndefined({
        session_id: draft.session_id,
        table_id: draft.table_id,
        table_number: draft.table_number,
        order_id: draft.order_id,
        original_order_total: draft.original_order_total,
        status: draft.status,
        active_step: draft.active_step,
        people: draft.people,
        selected_items: draft.selected_items,
        custom_amounts: draft.custom_amounts,
        payment_methods: draft.payment_methods,
        tip_amount: draft.tip_amount,
        custom_tip: draft.custom_tip,
        created_by: draft.created_by,
        updated_at: serverTimestamp(),
        last_accessed_at: serverTimestamp()
      });

      if (existingDraft && existingDraft.id) {
        // Update existing draft
        const draftRef = doc(db, this.COLLECTION_NAME, existingDraft.id);
        await updateDoc(draftRef, draftData);
        console.log('✅ [DRAFT] Draft updated in Firestore:', {
          id: existingDraft.id,
          session_id: draft.session_id,
          table_number: draft.table_number,
          status: draft.status,
          collection: this.COLLECTION_NAME
        });
        return existingDraft.id;
      } else {
        // Create new draft
        draftData.created_at = serverTimestamp();
        const docRef = await addDoc(collection(db, this.COLLECTION_NAME), draftData);
        console.log('✅ [DRAFT] New draft saved to Firestore:', {
          id: docRef.id,
          session_id: draft.session_id,
          table_number: draft.table_number,
          status: draft.status,
          collection: this.COLLECTION_NAME
        });
        return docRef.id;
      }
    } catch (error) {
      console.error('❌ [DRAFT] Error saving draft to Firestore:', error);
      console.error('❌ [DRAFT] Draft data that failed:', draft);
      throw error;
    }
  }

  /**
   * Get draft by session ID
   */
  public async getDraftBySessionId(sessionId: string): Promise<SplitTableDraft | null> {
    try {
      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('session_id', '==', sessionId)
      );
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return null;
      }
      
      const doc = querySnapshot.docs[0];
      return {
        id: doc.id,
        ...doc.data(),
        created_at: doc.data().created_at?.toDate?.()?.toISOString() || doc.data().created_at,
        updated_at: doc.data().updated_at?.toDate?.()?.toISOString() || doc.data().updated_at,
        last_accessed_at: doc.data().last_accessed_at?.toDate?.()?.toISOString() || doc.data().last_accessed_at
      } as SplitTableDraft;
    } catch (error) {
      console.error('❌ [DRAFT] Error getting draft:', error);
      return null;
    }
  }

  /**
   * Get ALL drafts (for debugging - regardless of status)
   */
  public async getAllDrafts(): Promise<SplitTableDraft[]> {
    try {
      console.log('📋 [DRAFT DEBUG] Fetching ALL drafts from Firestore...');
      const allDraftsSnapshot = await getDocs(collection(db, this.COLLECTION_NAME));
      const allDrafts = allDraftsSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          created_at: data.created_at?.toDate?.()?.toISOString() || data.created_at,
          updated_at: data.updated_at?.toDate?.()?.toISOString() || data.updated_at,
          last_accessed_at: data.last_accessed_at?.toDate?.()?.toISOString() || data.last_accessed_at
        } as SplitTableDraft;
      });
      
      console.log('📋 [DRAFT DEBUG] Found', allDrafts.length, 'total drafts');
      console.log('📋 [DRAFT DEBUG] Draft statuses:', allDrafts.map(d => ({ id: d.id, status: d.status, table: d.table_number })));
      return allDrafts;
    } catch (error) {
      console.error('❌ [DRAFT DEBUG] Error getting all drafts:', error);
      return [];
    }
  }

  /**
   * Get all active drafts (draft or pending status)
   */
  public async getActiveDrafts(): Promise<SplitTableDraft[]> {
    try {
      console.log('📋 [DRAFT] Fetching active drafts from Firestore...');
      
      // Try query with status filter first
      let querySnapshot;
    try {
      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('status', 'in', ['draft', 'pending'])
      );
        querySnapshot = await getDocs(q);
        console.log('✅ [DRAFT] Query successful, found', querySnapshot.size, 'drafts');
      } catch (queryError: any) {
        // If query fails (e.g., missing index), fallback to fetching all and filtering client-side
        if (queryError.code === 'failed-precondition') {
          console.warn('⚠️ [DRAFT] Query requires index, falling back to client-side filter');
          console.warn('⚠️ [DRAFT] Error:', queryError.message);
          
          // Fetch all drafts and filter client-side
          const allDraftsSnapshot = await getDocs(collection(db, this.COLLECTION_NAME));
          console.log('📋 [DRAFT] Fetched all drafts:', allDraftsSnapshot.size);
          
          // Filter client-side for active statuses
          const activeDrafts = allDraftsSnapshot.docs
            .map(doc => ({
              id: doc.id,
              ...doc.data(),
              created_at: doc.data().created_at?.toDate?.()?.toISOString() || doc.data().created_at,
              updated_at: doc.data().updated_at?.toDate?.()?.toISOString() || doc.data().updated_at,
              last_accessed_at: doc.data().last_accessed_at?.toDate?.()?.toISOString() || doc.data().last_accessed_at
            }))
            .filter((draft: any) => draft.status === 'draft' || draft.status === 'pending') as SplitTableDraft[];
          
          console.log('✅ [DRAFT] Filtered to', activeDrafts.length, 'active drafts');
          return activeDrafts;
        } else {
          throw queryError;
        }
      }
      
      const drafts = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          created_at: data.created_at?.toDate?.()?.toISOString() || data.created_at,
          updated_at: data.updated_at?.toDate?.()?.toISOString() || data.updated_at,
          last_accessed_at: data.last_accessed_at?.toDate?.()?.toISOString() || data.last_accessed_at
        } as SplitTableDraft;
      });
      
      console.log('✅ [DRAFT] Returning', drafts.length, 'active drafts');
      return drafts;
    } catch (error: any) {
      console.error('❌ [DRAFT] Error getting active drafts:', error);
      console.error('❌ [DRAFT] Error details:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      
      // Last resort: try to fetch all drafts without filter
      try {
        console.log('🔄 [DRAFT] Attempting fallback: fetch all drafts');
        const allDraftsSnapshot = await getDocs(collection(db, this.COLLECTION_NAME));
        const allDrafts = allDraftsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        created_at: doc.data().created_at?.toDate?.()?.toISOString() || doc.data().created_at,
        updated_at: doc.data().updated_at?.toDate?.()?.toISOString() || doc.data().updated_at,
        last_accessed_at: doc.data().last_accessed_at?.toDate?.()?.toISOString() || doc.data().last_accessed_at
      })) as SplitTableDraft[];
        
        // Filter client-side
        const activeDrafts = allDrafts.filter(draft => 
          draft.status === 'draft' || draft.status === 'pending'
        );
        
        console.log('✅ [DRAFT] Fallback successful, found', activeDrafts.length, 'active drafts');
        return activeDrafts;
      } catch (fallbackError) {
        console.error('❌ [DRAFT] Fallback also failed:', fallbackError);
      return [];
      }
    }
  }

  /**
   * Get active draft by table number (to prevent duplicates)
   */
  public async getActiveDraftByTable(tableNumber: string): Promise<SplitTableDraft | null> {
    try {
      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('table_number', '==', tableNumber),
        where('status', 'in', ['draft', 'pending'])
      );
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return null;
      }
      
      // Return the most recent draft (by updated_at)
      const drafts = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        created_at: doc.data().created_at?.toDate?.()?.toISOString() || doc.data().created_at,
        updated_at: doc.data().updated_at?.toDate?.()?.toISOString() || doc.data().updated_at,
        last_accessed_at: doc.data().last_accessed_at?.toDate?.()?.toISOString() || doc.data().last_accessed_at
      })) as SplitTableDraft[];
      
      // Sort by updated_at descending and return the most recent
      drafts.sort((a, b) => {
        const aTime = new Date(a.updated_at || a.created_at || 0).getTime();
        const bTime = new Date(b.updated_at || b.created_at || 0).getTime();
        return bTime - aTime;
      });
      
      return drafts[0] || null;
    } catch (error) {
      console.error('❌ [DRAFT] Error getting active draft by table:', error);
      return null;
    }
  }

  /**
   * Get drafts for a specific table
   */
  public async getDraftsByTable(tableNumber: string): Promise<SplitTableDraft[]> {
    try {
      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('table_number', '==', tableNumber),
        where('status', 'in', ['draft', 'pending'])
      );
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        created_at: doc.data().created_at?.toDate?.()?.toISOString() || doc.data().created_at,
        updated_at: doc.data().updated_at?.toDate?.()?.toISOString() || doc.data().updated_at,
        last_accessed_at: doc.data().last_accessed_at?.toDate?.()?.toISOString() || doc.data().last_accessed_at
      })) as SplitTableDraft[];
    } catch (error) {
      console.error('❌ [DRAFT] Error getting drafts by table:', error);
      return [];
    }
  }

  /**
   * Mark draft as completed
   */
  public async completeDraft(draftId: string): Promise<void> {
    try {
      const draftRef = doc(db, this.COLLECTION_NAME, draftId);
      await updateDoc(draftRef, {
        status: 'completed',
        updated_at: serverTimestamp()
      });
      console.log('✅ [DRAFT] Draft marked as completed:', draftId);
    } catch (error) {
      console.error('❌ [DRAFT] Error completing draft:', error);
      throw error;
    }
  }

  /**
   * Cancel/delete a draft
   */
  public async cancelDraft(draftId: string): Promise<void> {
    try {
      const draftRef = doc(db, this.COLLECTION_NAME, draftId);
      await updateDoc(draftRef, {
        status: 'cancelled',
        updated_at: serverTimestamp()
      });
      console.log('✅ [DRAFT] Draft cancelled:', draftId);
    } catch (error) {
      console.error('❌ [DRAFT] Error cancelling draft:', error);
      throw error;
    }
  }

  /**
   * Cancel ALL active drafts for a specific table.
   * Used after completing a split to prevent stale drafts showing in Dashboard.
   */
  public async cancelAllDraftsForTable(tableNumber: string): Promise<void> {
    try {
      const drafts = await this.getDraftsByTable(tableNumber);
      if (drafts.length === 0) {
        console.log(`✅ [DRAFT] No active drafts to cancel for table ${tableNumber}`);
        return;
      }
      await Promise.all(
        drafts.map(draft => {
          if (!draft.id) return Promise.resolve();
          const draftRef = doc(db, this.COLLECTION_NAME, draft.id);
          return updateDoc(draftRef, { status: 'completed', updated_at: serverTimestamp() });
        })
      );
      console.log(`✅ [DRAFT] Cancelled ${drafts.length} active draft(s) for table ${tableNumber}`);
    } catch (error) {
      console.error('❌ [DRAFT] Error cancelling all drafts for table:', error);
      throw error;
    }
  }

  /**
   * Delete a draft permanently
   */
  public async deleteDraft(draftId: string): Promise<void> {
    try {
      await deleteDoc(doc(db, this.COLLECTION_NAME, draftId));
      console.log('✅ [DRAFT] Draft deleted:', draftId);
    } catch (error) {
      console.error('❌ [DRAFT] Error deleting draft:', error);
      throw error;
    }
  }

  /**
   * Subscribe to active drafts (real-time updates)
   */
  public subscribeToActiveDrafts(callback: (drafts: SplitTableDraft[]) => void): () => void {
    let unsubscribe: (() => void) | null = null;
    let fallbackUnsubscribe: (() => void) | null = null;
    
    try {
    const q = query(
      collection(db, this.COLLECTION_NAME),
      where('status', 'in', ['draft', 'pending'])
    );
    
      unsubscribe = onSnapshot(q, 
        (querySnapshot) => {
          const drafts = querySnapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              created_at: data.created_at?.toDate?.()?.toISOString() || data.created_at,
              updated_at: data.updated_at?.toDate?.()?.toISOString() || data.updated_at,
              last_accessed_at: data.last_accessed_at?.toDate?.()?.toISOString() || data.last_accessed_at
            } as SplitTableDraft;
          });
          
          console.log('📋 [DRAFT] Real-time update:', drafts.length, 'active drafts');
          callback(drafts);
        },
        (error: any) => {
          console.error('❌ [DRAFT] Real-time listener error:', error);
          
          // Fallback: subscribe to all drafts and filter client-side
          if (error.code === 'failed-precondition') {
            console.warn('⚠️ [DRAFT] Real-time query requires index, using fallback');
            fallbackUnsubscribe = onSnapshot(
              collection(db, this.COLLECTION_NAME),
              (snapshot) => {
                const allDrafts = snapshot.docs.map(doc => {
                  const data = doc.data();
                  return {
        id: doc.id,
                    ...data,
                    created_at: data.created_at?.toDate?.()?.toISOString() || data.created_at,
                    updated_at: data.updated_at?.toDate?.()?.toISOString() || data.updated_at,
                    last_accessed_at: data.last_accessed_at?.toDate?.()?.toISOString() || data.last_accessed_at
                  } as SplitTableDraft;
                });
                
                // Filter client-side
                const activeDrafts = allDrafts.filter(draft => 
                  draft.status === 'draft' || draft.status === 'pending'
                );
                
                console.log('📋 [DRAFT] Real-time fallback update:', activeDrafts.length, 'active drafts');
                callback(activeDrafts);
              }
            );
          }
        }
      );
      
      return () => {
        if (unsubscribe) unsubscribe();
        if (fallbackUnsubscribe) fallbackUnsubscribe();
      };
    } catch (error) {
      console.error('❌ [DRAFT] Error setting up real-time listener:', error);
      
      // Fallback: subscribe to all drafts
      fallbackUnsubscribe = onSnapshot(
        collection(db, this.COLLECTION_NAME),
        (snapshot) => {
          const allDrafts = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              created_at: data.created_at?.toDate?.()?.toISOString() || data.created_at,
              updated_at: data.updated_at?.toDate?.()?.toISOString() || data.updated_at,
              last_accessed_at: data.last_accessed_at?.toDate?.()?.toISOString() || data.last_accessed_at
            } as SplitTableDraft;
          });
          
          // Filter client-side
          const activeDrafts = allDrafts.filter(draft => 
            draft.status === 'draft' || draft.status === 'pending'
          );
          
          console.log('📋 [DRAFT] Fallback real-time update:', activeDrafts.length, 'active drafts');
          callback(activeDrafts);
        }
      );
      
      return () => {
        if (fallbackUnsubscribe) fallbackUnsubscribe();
      };
    }
  }
}

export default SplitTableDraftService;

